import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Icon } from '../icon';

import styles from './scrollable-collection.scss';
import { TEMPLATES } from './templates';
import { Grid, ScrollDirection } from './types';

const GRID_GAP = '8px';

const COMPONENT_ID = 'modulor-scrollable-collection';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    children: ReactElement;
    dataTestId?: string;
  };

const MAXIMUM_SIZE = 20;
const MOBILE_WIDTH = 1023;

export const ScrollableCollection: ForwardRefExoticComponent<Props> = forwardRef<
  HTMLDivElement,
  Props
>(({ children, onClick, dataTestId = COMPONENT_ID, className, ...rest }, ref) => {
  const gridRef = useRef<HTMLDivElement>(null);

  const [controlsVisibility, setControlsVisibility] = useState({
    left: false,
    right: false,
  });

  const [scrollLength, setScrollLength] = useState(0);
  const [isMobile, setMobile] = useState(window?.innerWidth <= MOBILE_WIDTH);

  const childrenArray = React.Children.toArray(children) as ReactElement[];

  const templateIndex = Math.min(React.Children.count(children), MAXIMUM_SIZE);
  const rows: Grid = TEMPLATES[templateIndex];

  const handleControlClick = useCallback(
    (direction: ScrollDirection) => {
      const { clientWidth, scrollWidth } = gridRef.current;

      if (direction === 'left') {
        setScrollLength(Math.max(0, scrollLength - clientWidth));
      }

      if (direction === 'right') {
        setScrollLength(Math.min(scrollLength + clientWidth, scrollWidth));
      }
    },
    [scrollLength],
  );

  const handleResize = useCallback(() => {
    if (typeof window === 'undefined') return;

    setMobile(window.innerWidth <= MOBILE_WIDTH);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    gridRef.current.scroll({
      left: scrollLength,
      behavior: 'smooth',
    });

    const { clientWidth, scrollWidth } = gridRef.current;

    const scrollableByControls = clientWidth < scrollWidth && !isMobile;

    setControlsVisibility({
      left: scrollLength > 0 && scrollableByControls,
      right: scrollLength + clientWidth < scrollWidth && scrollableByControls,
    });
  }, [scrollLength, isMobile]);

  return (
    <div
      data-id={dataTestId}
      data-testid={dataTestId}
      data-component-name={COMPONENT_ID}
      className={classNames(className, styles.wrapper)}
      ref={ref}
      {...rest}>
      <div onClick={onClick} ref={gridRef} className={styles.grid}>
        {rows.map((row, rowIndex) => (
          <div className={styles.row} key={rowIndex} data-testid={`${dataTestId}-row-${rowIndex}`}>
            {row.map((size, index) =>
              React.cloneElement(childrenArray.shift(), {
                style: {
                  width: `calc(var(--base-item-size) * ${size} + ${size - 1} * ${GRID_GAP})`,
                },
                className: styles.item,
                dataTestId: `${dataTestId}-item-${index}`,
              }),
            )}
          </div>
        ))}
      </div>
      <div className={styles['controls-wrapper']}>
        {controlsVisibility.left && (
          <button
            className={classNames(styles.control, styles.left)}
            onClick={() => handleControlClick('left')}>
            <Icon name="chevron_left" />
          </button>
        )}
        {controlsVisibility.right && (
          <button
            className={classNames(styles.control, styles.right)}
            onClick={() => handleControlClick('right')}>
            <Icon name="chevron_right" />
          </button>
        )}
      </div>
    </div>
  );
});

ScrollableCollection.displayName = 'ScrollableCollection';
